/**
 * Shared internationalized formatting utilities using native browser Intl APIs.
 */

/**
 * Formats currency amounts using native Intl.NumberFormat.
 * Supports compact notation (e.g. "$120K") and standard notation.
 */
export function formatCurrencyAmount(
  amount: number,
  currency: string = "USD",
  locale: string = "en-US",
  compact: boolean = true,
): string {
  const trimmed = currency.trim();
  const isIsoCode = /^[A-Z]{3}$/i.test(trimmed);
  const isoCode = isIsoCode ? trimmed.toUpperCase() : "USD";
  const customSymbol = !isIsoCode && trimmed ? trimmed : null;

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: isoCode,
      maximumFractionDigits: 0,
      notation: compact && amount >= 1000 ? "compact" : "standard",
      compactDisplay: "short",
    });

    let formatted = formatter.format(amount);
    if (customSymbol) {
      formatted = formatted.replace(/[A-Z]{3}/g, customSymbol);
    }
    return formatted;
  } catch {
    return `${customSymbol || "$"}${amount.toLocaleString(locale)}`;
  }
}

/**
 * Formats compensation range (min, max, currency, period) using Intl.NumberFormat.
 * e.g. "$140K – $180K / year", "From $120K / year", "Up to $200K / year"
 */
export function formatComp(
  min?: number | null,
  max?: number | null,
  currency?: string | null,
  period?: string | null,
  locale: string = "en-US",
): string | null {
  if (min == null && max == null) return null;
  const per = period ? ` / ${period}` : "";
  const curr = currency || "USD";

  if (min != null && max != null) {
    const minStr = formatCurrencyAmount(min, curr, locale, true);
    const maxStr = formatCurrencyAmount(max, curr, locale, true);
    return `${minStr} - ${maxStr}${per}`;
  }
  if (min != null) {
    const minStr = formatCurrencyAmount(min, curr, locale, true);
    return `From ${minStr}${per}`;
  }
  if (max != null) {
    const maxStr = formatCurrencyAmount(max, curr, locale, true);
    return `Up to ${maxStr}${per}`;
  }
  return null;
}

/**
 * Formats relative time using browser native Intl.RelativeTimeFormat.
 * e.g. "today", "yesterday", "3 days ago", "2 months ago"
 */
export function formatRelativeTime(
  dateInput?: Date | string | number | null,
  locale: string = "en-US",
): string {
  if (!dateInput) return "Recently";
  const date =
    typeof dateInput === "object" && dateInput instanceof Date ? dateInput : new Date(dateInput);

  if (isNaN(date.getTime())) return "Recently";

  const now = new Date();
  const diffSec = Math.round((date.getTime() - now.getTime()) / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);
  const diffMonth = Math.round(diffDay / 30);
  const diffYear = Math.round(diffDay / 365);

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (Math.abs(diffSec) < 60) return "just now";
  if (Math.abs(diffMin) < 60) return formatter.format(diffMin, "minute");
  if (Math.abs(diffHour) < 24) return formatter.format(diffHour, "hour");
  if (Math.abs(diffDay) < 30) return formatter.format(diffDay, "day");
  if (Math.abs(diffMonth) < 12) return formatter.format(diffMonth, "month");
  return formatter.format(diffYear, "year");
}

/**
 * Formats experience requirements range with Intl.PluralRules.
 * e.g. "3–5 yrs exp", "5+ yrs exp", "1 yr exp"
 */
export function formatExperience(
  min?: number | null,
  max?: number | null,
  locale: string = "en-US",
): string | null {
  if (min == null && max == null) return null;
  const pr = new Intl.PluralRules(locale);

  if (min != null && max != null) {
    if (min === max) {
      const suffix = pr.select(min) === "one" ? "yr" : "yrs";
      return `${min} ${suffix} exp`;
    }
    return `${min}–${max} yrs exp`;
  }
  if (min != null) {
    const suffix = pr.select(min) === "one" ? "yr" : "yrs";
    return `${min}+ ${suffix} exp`;
  }
  if (max != null) {
    const suffix = pr.select(max) === "one" ? "yr" : "yrs";
    return `Up to ${max} ${suffix} exp`;
  }
  return null;
}

/**
 * Extracts 1-2 letter uppercase initials from a name or email.
 */
export function getInitials(name?: string | null): string {
  if (!name?.trim()) return "U";
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Formats a calendar date using native Intl.DateTimeFormat.
 */
export function formatDate(
  dateInput?: Date | string | number | null,
  locale: string = "en-US",
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
): string {
  if (!dateInput) return "";
  const date =
    typeof dateInput === "object" && dateInput instanceof Date ? dateInput : new Date(dateInput);

  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, options).format(date);
}

/**
 * Converts a 2-letter ISO country code to a Unicode flag emoji.
 * e.g. "US" -> "🇺🇸", "IN" -> "🇮🇳", "GB" -> "🇬🇧", "CA" -> "🇨🇦"
 */
export function getCountryFlag(countryCode?: string | null): string {
  if (!countryCode) return "";
  const code = countryCode.trim().toUpperCase();
  if (code === "REMOTE" || code === "GLOBAL" || code === "WW") return "🌐";
  if (code.length !== 2) return "";
  const first = code.charCodeAt(0);
  const second = code.charCodeAt(1);
  if (first < 65 || first > 90 || second < 65 || second > 90) return "";
  return String.fromCodePoint(127397 + first, 127397 + second);
}

/**
 * Gets a formatted English country name from a 2-letter ISO code.
 * e.g. "US" -> "United States", "IN" -> "India"
 */
export function getCountryName(countryCode?: string | null): string {
  if (!countryCode) return "";
  const code = countryCode.trim().toUpperCase();
  try {
    const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
}
